import React, { ReactNode, createRef, Fragment } from "react";
import Downshift, { DownshiftProps } from "downshift";
import Input, { InputProps } from "../input/Input";
import Box, { BoxProps } from "../box/Box";
import SvgBox from "../box/SvgBox";
import CloseButton from "../CloseButton";
import Text from "../typography/Text";

export interface IFilterSelectOption {
  section?: undefined;
  label: string;
  value: string;
}

export type TFilterSelectItem = { section: string; options: IFilterSelectOption[] } | IFilterSelectOption;

interface IFilterSelectProps {
  placeholder?: string;
  items: TFilterSelectItem[];
  disabled?: boolean;
  getMenuProps?: () => Record<string, any>;
  getInputProps?: () => Record<string, any>;
  getItemProps?: (globalIndex: number, option: IFilterSelectOption) => Record<string, any>;
}

export default function FilterSelect({
  items,
  placeholder,
  getMenuProps: getMenuPropsFn = () => ({}),
  getInputProps: getInputPropsFn = () => ({}),
  getItemProps: getItemPropsFn = () => ({}),
  disabled = false,
  ...props
}: IFilterSelectProps & DownshiftProps<IFilterSelectOption>) {
  const inputRef = createRef<HTMLInputElement>();

  return (
    <Downshift itemToString={item => item?.label || ""} {...props}>
      {({
        getInputProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        isOpen,
        openMenu,
        inputValue,
        clearSelection,
        selectedItem,
      }) => {
        let globalIndex = -1;
        const filteredItems = filterItems(items, inputValue || "");

        return (
          <div>
            <div css={{ position: "relative" }}>
              <Input
                placeholder={placeholder}
                {...getInputProps<InputProps>(getInputPropsFn())}
                size="sm"
                pr={6}
                disabled={disabled}
                readOnly={disabled}
                ref={inputRef}
                autoComplete="off"
              />

              {selectedItem ? (
                <CloseButton
                  css={{
                    position: "absolute",
                    right: 4,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                  aria-label="Close menu"
                  onClick={() => {
                    clearSelection();

                    inputRef.current?.focus();
                  }}
                  disabled={disabled}
                />
              ) : (
                <button
                  aria-label="Open menu"
                  css={{
                    position: "absolute",
                    right: 4,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                  disabled={disabled}
                  type="button"
                  onClick={() => {
                    if (inputRef.current) {
                      inputRef.current.focus();
                    }

                    openMenu();
                  }}
                >
                  <SvgBox viewBox="0 0 24 24" width={6} height={6} color={isOpen ? "blue.600" : "gray.600"}>
                    <rect width="24" height="24" rx="6" fill="currentColor" />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M21.363 8.14973C21.7544 8.83215 21.6106 9.76611 21.0419 10.2358L12.4949 17.2946C12.059 17.6546 11.4814 17.6462 11.0529 17.2737L2.93324 10.2149C2.37418 9.72885 2.2493 8.791 2.65432 8.12012C3.05934 7.44924 3.84088 7.29939 4.39994 7.78541L11.8047 14.2228L19.6246 7.7645C20.1933 7.29483 20.9716 7.4673 21.363 8.14973Z"
                      fill="white"
                    />
                  </SvgBox>
                </button>
              )}
            </div>

            {isOpen && (
              <div css={{ position: "relative" }}>
                <Box
                  position="absolute"
                  width="100%"
                  top={2}
                  left={0}
                  {...getMenuProps(getMenuPropsFn())}
                  as="div"
                  zIndex="dropdown"
                  boxShadow="lg"
                  border="1px solid"
                  borderColor="gray.200"
                  bg="white"
                  listStyleType="none"
                  maxHeight={300}
                  overflowY="auto"
                  borderRadius="sm"
                >
                  {filteredItems.length === 0 && (
                    <Text color="gray.900" m={0} fontSize="sm" p={3}>
                      No results found...
                    </Text>
                  )}
                  {filteredItems.map(item => {
                    if (item.section !== undefined) {
                      return (
                        <Fragment key={item.section}>
                          <FilterSelectSection>{item.section}</FilterSelectSection>
                          {item.options.map(option => {
                            globalIndex += 1;

                            return (
                              <FilterSelectOption
                                active={highlightedIndex === globalIndex}
                                key={option.value}
                                {...getItemProps({
                                  ...getItemPropsFn(globalIndex, option),
                                  index: globalIndex,
                                  item: option,
                                })}
                              >
                                {option.label}
                              </FilterSelectOption>
                            );
                          })}
                        </Fragment>
                      );
                    }

                    globalIndex += 1;

                    return (
                      <FilterSelectOption
                        key={item.value}
                        active={highlightedIndex === globalIndex}
                        {...getItemProps({
                          index: globalIndex,
                          item,
                        })}
                      >
                        {item.label}
                      </FilterSelectOption>
                    );
                  })}
                </Box>
              </div>
            )}
          </div>
        );
      }}
    </Downshift>
  );
}

function filterItems(items: TFilterSelectItem[], inputValue: string): TFilterSelectItem[] {
  const regex = new RegExp(`^${inputValue}`, "i");

  return items
    .map(item => {
      if (item.section) {
        return {
          ...item,
          options: item.options.filter(option => regex.test(option.label)),
        };
      }

      return item;
    })
    .filter(item => {
      if (item.section !== undefined) {
        return item.options.length > 0;
      }

      return regex.test(item.label);
    });
}

interface IFilterSelectSectionProps {
  children: ReactNode;
}

function FilterSelectSection({ children }: IFilterSelectSectionProps) {
  return (
    <Box px={4} py={2} color="gray.900" fontWeight="semibold">
      {children}
    </Box>
  );
}

interface IFilterSelectOptionProps extends BoxProps {
  active?: boolean;
}

function FilterSelectOption({ children, active, ...props }: IFilterSelectOptionProps) {
  return (
    <Box
      as="li"
      color={active ? "blue.600" : "gray.900"}
      px={4}
      pl={6}
      py={2}
      fontSize="sm"
      _hover={{
        color: "blue.600",
      }}
      cursor="pointer"
      {...props}
    >
      {children}
    </Box>
  );
}
